/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { Flex, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { CapacityPlanningAction } from './types';

type Props = {
	actions: CapacityPlanningAction[];
	getStatus: (action: CapacityPlanningAction) => string;
};

const allActionsAcceptedMessage = defineMessage({
	id: 'capacity-planning.all-actions-accepted.non-final',
	defaultMessage: "You've accepted all actions",
	description: 'Message indicating that all actions have been accepted.',
});
const allActionsDismissedMessage = defineMessage({
	id: 'capacity-planning.all-actions-dismissed.non-final',
	defaultMessage: "You've dismissed all actions",
	description: 'Message indicating that all actions have been dismissed.',
});

const styles = cssMap({
	explainerContainer: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		gap: token('space.150'),
	},
	explainer: {
		paddingTop: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		gap: token('space.075'),
		borderRadius: token('radius.full'),
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
});

export function SuccessMessage({ actions, getStatus }: Props): React.JSX.Element {
	const { formatMessage } = useIntl();

	const hasAcceptedActions = actions.some(
		(action) => getStatus(action) === 'finished' || action.status === 'finished',
	);

	const message = hasAcceptedActions
		? formatMessage(allActionsAcceptedMessage)
		: formatMessage(allActionsDismissedMessage);

	return (
		<Flex
			xcss={styles.explainerContainer}
			alignItems="center"
			justifyContent="center"
			direction="column"
		>
			<Inline xcss={styles.explainer} alignBlock="center" alignInline="center">
				<CheckMarkIcon label="" color={token('color.text.success')} />
				<Text>{message}</Text>
			</Inline>
		</Flex>
	);
}
